import { Component, inject, Injector, OnDestroy, OnInit, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { Application } from '@modules/common/types/application';
import { Stitch } from '@modules/common/types/stitch';
import { Tab } from '@modules/common/types/tab';
import { ContactListStateService } from '@modules/contacts/services/contact-list-state.service';
import { ContactsTenantScopeService } from '@modules/contacts/services/contacts-tenant-scope.service';
import { GroupListStateService } from '@modules/contacts/services/group-list-state.service';
import { ContactsAppStateService } from '@modules/contacts/services/state.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { Contact } from '@modules/contacts/types/contact';
import { ContactsFilters } from '@modules/contacts/types/contacts-filters';
import { Group } from '@modules/contacts/types/group';
import { GroupsFilters } from '@modules/contacts/types/groups-filters';
import { type ListState } from '@modules/contacts/types/list-state';
import { VirtualFolder } from '@modules/contacts/types/virtual-folder';
import { isCreationAllowedInVirtualFolder } from '@modules/common/utils/virtual-folder';
import { DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { ModalService } from '@modules/modal/services/modal.service';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateKey } from '@modules/settings/types/state-key';
import { SpaceType } from '@sharedlib/common/types/SpaceType';
import { StitchType } from '@sharedlib/common/types/StitchType';
import { combineLatest, of, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-contacts-panel',
  templateUrl: './contacts-panel.component.html',
  styleUrls: ['./contacts-panel.component.less'],
  standalone: false,
  providers: [
    ...ContactListStateService.providers({
      stateKey: StateKey.contactsDPListState,
      withAdvancedSearch: true,
    }),
    ...GroupListStateService.providers({
      stateKey: StateKey.groupsDPListState,
      withAdvancedSearch: true,
    }),
  ],
})
export class ContactsPanelComponent implements OnInit, OnDestroy {
  /**
   * VIP selects contacts already marked as such, so a freshly created one never lands there — the
   * creation form is hidden on that page (DP-001).
   */
  private static readonly NON_CREATABLE_FOLDERS: ReadonlySet<VirtualFolder> = new Set<VirtualFolder>(['vip']);

  public groupsFilters: GroupsFilters;
  public contactsFilters: ContactsFilters;
  protected quickContact: Contact;
  protected quickGroup: Group;
  protected listTenantItems: ReadonlyArray<{ readonly accountId?: string }> | undefined;
  public contextMenuOpened: boolean;
  public selectedFilter: VirtualFolder;
  public listState: ListState;
  public tabs: Tab[] = [
    { title: 'Groups', value: 'groups' },
    { title: 'Contacts', value: 'contacts' },
  ];
  public selectedTab: Exclude<ListState, 'tabs'> = 'contacts';
  protected searchTenantItems: ReadonlyArray<{ readonly accountId?: string }> | undefined;

  /**
   * Derived pages offer no creation form — see {@link ContactsPanelComponent.NON_CREATABLE_FOLDERS}.
   * Derived from the stored page, not from `selectFilter`: that method early-returns while an
   * ids-only search is in flight, which would leave a mirrored flag disagreeing with the list.
   */
  protected readonly creationAllowed: Signal<boolean>;

  protected readonly popoverClose = new Subject<void>();
  private alive = new Subject<void>();
  private readonly contactListStateService = inject(ContactListStateService, { self: true });
  private readonly groupListStateService = inject(GroupListStateService, { self: true });
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  protected readonly contactsTenantScope = inject(ContactsTenantScopeService, { optional: true });
  private readonly spaceContextStore = inject(SpaceContextStore);

  private readonly selectedTenant$ = this.contactsTenantScope?.selectedTeamId?.() ?? of(null);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;

  /** Location badges are shown on contact rows only while an advanced search is applied. */
  protected readonly isAdvancedSearchApplied: Signal<boolean>;
  /**
   * Search-shaped: the shared query/markers, transient ids, and this app's form-only filters (company, job
   * title, country, email, phone) — nothing else reports those, so this pill must. The date range and
   * `stitched` are excluded on purpose: they light the list filter menu's funnel pill instead.
   */
  protected readonly isSearchApplied: Signal<boolean>;

  constructor(
    private dpService: DynamicPanelService,
    private modalService: ModalService,
    private injector: Injector,
    private searchService: AdvancedSearchService,
    private stateService: ContactsAppStateService,
  ) {
    this.isAdvancedSearchApplied = toSignal(this.searchService.isAdvancedSearchApplied(Application.contacts), {
      initialValue: false,
    });
    this.isSearchApplied = toSignal(this.searchService.isSearchApplied(Application.contacts), { initialValue: false });
    this.creationAllowed = toSignal(
      this.stateService
        .getVirtualFolder()
        .pipe(map((folder) => isCreationAllowedInVirtualFolder(folder, ContactsPanelComponent.NON_CREATABLE_FOLDERS))),
      { initialValue: true },
    );
  }

  /**
   * Lifecycle
   */

  ngOnInit(): void {
    // Inside a team an untouched panel lists the TEAM's contacts (the prototype's companion), never the
    // member's private corner. Stored before the lists subscribe, so the identity lookup keeps it.
    const scope = this.contactsTenantScope;
    const team =
      this.spaceContextStore.type() === SpaceType.SHARED_SPACE ? this.spaceContextStore.contextId() : undefined;
    if (team && scope && scope.tenantId() === undefined) {
      scope.selectTenant(team);
    }

    this.stateService
      .getTabs()
      .pipe(takeUntil(this.alive))
      .subscribe((state: ListState) => {
        this.listState = state;
        if (this.listState !== 'tabs') {
          this.selectedTab = this.listState;
        }
      });

    // Check for pending search on init (handles race condition when panel wasn't initialized yet)
    const pending = this.dpService.consumePendingSearchByIds();
    if (pending?.stitchType === StitchType.CONTACT && pending?.ids?.length > 0) {
      this.applyIdsSearch(pending);
    }

    // Subscribe for future events (when panel is already active)
    this.dpService
      .onOpenSearchByIds()
      .pipe(
        filter(
          (payload) =>
            payload?.stitchType === StitchType.CONTACT && Array.isArray(payload?.ids) && payload.ids.length > 0,
        ),
        takeUntil(this.alive),
      )
      .subscribe((payload) => {
        this.applyIdsSearch(payload);
      });

    combineLatest([
      this.contactListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.selectedTenant$,
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone, tenantId]) => {
        // `displayTimeZone` FIRST: applyListState/applyVirtualFolder read it to reckon their day
        // windows in the account zone (SBUX-829).
        this.listTenantItems = tenantId ? [{ accountId: tenantId }] : undefined;
        this.quickContact = Contact.fromAdvancedState(search, folder);
        this.quickGroup = Group.fromAdvancedState(search, folder);
        this.quickContact.accountId = tenantId ?? undefined;
        this.quickGroup.accountId = tenantId ?? undefined;
        this.contactsFilters = new ContactsFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder, !!tenantId);
      });

    combineLatest([
      this.groupListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.selectedTenant$,
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone, tenantId]) => {
        this.listTenantItems = tenantId ? [{ accountId: tenantId }] : undefined;
        this.groupsFilters = new GroupsFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });
  }

  ngOnDestroy(): void {
    this.alive.next();
    this.alive.complete();
  }

  private applyIdsSearch(payload: {
    stitchType: StitchType;
    ids: string[];
    tenantItems?: ReadonlyArray<{ readonly accountId?: string }>;
  }): void {
    this.searchTenantItems = payload.tenantItems;
    const owners = new Set(payload.tenantItems?.map((item) => item.accountId));
    if (owners.size === 1 && !owners.has(undefined)) {
      this.contactsTenantScope?.selectTenant(payload.tenantItems[0].accountId);
    }
    this.stateService.setSearchResultsFolder();
    this.searchService.searchByIds(payload.stitchType, payload.ids);
  }

  /**
   * Actions
   */

  /**
   * "Add details" on the group quick form. Routed through the modal like the other five container forms,
   * not into the panel: `ModalService` is where the new item's opening tab is chosen, and a group created
   * here would otherwise land on its Contacts tab — empty, because the group does not exist yet.
   */
  protected handleNewGroup(group: Stitch): void {
    this.modalService.openFullForm(group, this.injector);
  }

  protected openContactInModal(contact: Stitch): void {
    this.modalService.openFullForm(contact, this.injector);
  }

  openContact(contact: Contact): void {
    this.dpService.setFormItem(contact);
  }

  openGroup(group: Group): void {
    this.dpService.setFormItem(group);
  }

  selectFilter(folder: VirtualFolder): void {
    // Skip if we're in IDs search mode and trying to set a folder other than search_results
    // This prevents the stored folder from localStorage from overriding our programmatic selection
    if (this.dpService.isIdsSearchMode() && folder !== 'search_results') {
      return;
    }

    this.selectedFilter = folder;
    this.stateService.setVirtualFolder(folder);
  }

  protected handleSearch(): void {
    this.popoverClose.next();
  }

  protected clearSearch(): void {
    this.searchTenantItems = undefined;
    // Only the search terms: the per-app date/stitched filters belong to the filters menu and its pill.
    this.searchService.resetSearchTerms();
    // The transient "View Contacts" ids are part of those terms, so the mode they opened ends here —
    // at the owner, independently of whether the folder-menu directive is mounted to notice.
    this.dpService.clearIdsSearchMode();
  }

  protected handleCancel(): void {
    this.popoverClose.next();
  }
}
